import { AttachmentService, type AttachmentDetails } from "@/services/AttachmenteService";
import { type Dispatch, type SetStateAction, useEffect } from "react";

function keepLocalPreviews(
  current: AttachmentDetails[],
  listed: AttachmentDetails[],
): AttachmentDetails[] {
  const currentById = new Map(current.map((attachment) => [attachment.id, attachment]));
  const listedIds = new Set(listed.map((attachment) => attachment.id));
  return [
    ...current.filter((attachment) => !listedIds.has(attachment.id)),
    ...listed.map((server) => {
      const local = currentById.get(server.id);
      if (
        local &&
        server.isProcessing &&
        (local.url.startsWith("blob:") || local.url.startsWith("data:"))
      ) {
        return { ...server, url: local.url, thumbnailUrl: local.thumbnailUrl };
      }
      return server;
    }),
  ];
}

export function useProcessingAttachmentRefresh(
  attachments: AttachmentDetails[],
  setAttachments?: Dispatch<SetStateAction<AttachmentDetails[]>>,
) {
  const hasProcessing = attachments.some((attachment) => attachment.isProcessing);
  const entityId = attachments.find((attachment) => attachment.entityId)?.entityId;

  useEffect(() => {
    if (!hasProcessing || !entityId || !setAttachments) {
      return;
    }

    let cancelled = false;
    const interval = window.setInterval(async () => {
      try {
        const listed = await AttachmentService.getByEntityId(entityId);
        if (!cancelled) {
          setAttachments((current) => keepLocalPreviews(current, listed));
        }
      } catch {
        // Keep the current attachments until the next poll.
      }
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [entityId, hasProcessing, setAttachments]);
}
